import { useState } from 'react';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { HeartHandshake, Leaf, Users, ChevronDown, PawPrint } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const communityItems: { el: string; en: string }[] = [
  {
    el: 'Τον Ιούλιο του 2008 προσφέραμε το ποσό των 11.000€ στην κοινότητα Καμάρες Σίφνου για την επισκευή γηπέδου 5×5 το οποίο συντελεί στην αναβάθμιση της περιοχής και χρησιμοποιείται από τους κατοίκους του νησιού. Η προσφορά γίνεται στην μνήμη του ιδρυτή Νίκου Θεοδωράτου.',
    en: 'In July 2008 we donated €11,000 to the community of Kamares, Sifnos, for the renovation of a 5×5 football court that upgrades the area and is used by the islanders. The donation was made in memory of our founder, Nikos Theodoratos.',
  },
  {
    el: 'Το Δεκέμβριο 2008, ευαισθητοποιημένοι απέναντι στους συμπολίτες μας που πλήγηκαν από τις πυρκαγιές στην περιοχή της Αρκαδίας, διανέμαμε δώρα και παιχνίδια με συμμετοχή του προσωπικού μας, στα παιδιά του Δημοτικού σχολείου, στο χωριό Βαρβάσαινα.',
    en: 'In December 2008, in solidarity with the citizens affected by the wildfires in Arcadia, our staff distributed gifts and toys to the children of the primary school in the village of Varvasaina.',
  },
  {
    el: 'Τον Απρίλιο 2011, εκπαιδεύσαμε το προσωπικό μας στην παροχή Πρώτων Βοηθειών παρέχοντας του διήμερο σεμινάριο στους χώρους μας με παροχή πιστοποίησης (Emergency First Response).',
    en: 'In April 2011 we trained our staff in First Aid through a two-day on-site seminar with certification (Emergency First Response).',
  },
  {
    el: 'Το καλοκαίρι του 2012 προσφέραμε το ποσό των 15.000€ στην εταιρεία κοινωνικής μέριμνας του Δήμου Σίφνου για την εύρυθμη λειτουργία του παιδικού σταθμού «Το μικρό καράβι».',
    en: 'In summer 2012 we donated €15,000 to the social welfare company of the Municipality of Sifnos for the smooth operation of the "Little Boat" nursery school.',
  },
  {
    el: 'Την ίδια χρονιά αναλάβαμε ως χορηγός τον πλήρη εξοπλισμό στην ποδοσφαιρική ομάδα της Σίφνου Πανσιφνιακός, προσφορά την οποία συνεχίζουμε ως και σήμερα.',
    en: 'That same year we became the sponsor providing the full equipment of the Sifnos football team, Pansifniakos — a sponsorship we continue to this day.',
  },
  {
    el: 'Το 2017 για πρώτη φορά δημιουργήσαμε τον θεσμό «Sifnos Run» η οποία στέφτηκε με απόλυτη επιτυχία και συνεχίζει και το 2018.',
    en: 'In 2017 we launched the "Sifnos Run" institution for the first time, which was a great success and continued in 2018.',
  },
  {
    el: 'Σε ετήσια βάση προσφέρουμε βοήθεια σε οργανώσεις για παιδιά που έχουν ανάγκη όπως στα Παιδικά Χωριά SOS, το Χαμόγελο του Παιδιού και το εργαστήριο ειδικής αγωγής Μαργαρίτα.',
    en: 'On an annual basis we support organizations for children in need such as SOS Children\'s Villages, The Smile of the Child and the Margarita special education workshop.',
  },
];

const environmentItems: { el: string; en: string }[] = [
  {
    el: 'Εγκαταστήσαμε ένα από τα πρώτα διασυνδεδεμένα φωτοβολταϊκά συστήματα σε κτίριο στην Ελλάδα, στην οροφή του ιδιοκτήτου κτιρίου που στεγάζονται τα γραφεία της εταιρείας μας.',
    en: 'We installed one of the first grid-connected photovoltaic systems on a building in Greece, on the rooftop of the company-owned building that houses our offices.',
  },
  {
    el: 'Αντικαταστήσαμε όλες τις συμβατικές λάμπες με LED.',
    en: 'We replaced all conventional lamps with LED lighting.',
  },
  {
    el: 'Εγκαταστήσαμε σύστημα εξοικονόμησης ενέργειας.',
    en: 'We installed an energy saving system.',
  },
  {
    el: 'Εφαρμόζουμε σταθερά προγράμματα και συνεργαζόμαστε με εταιρείες ανακύκλωσης για μπαταρίες, μελάνια εκτυπωτών, λάμπες, H/Y, χρησιμοποιώντας τους ειδικούς τοποθετημένους μπλε κάδους ανακύκλωσης χαρτιού, αλουμινίου και γυαλιού, στους χώρους των γραφείων μας.',
    en: 'We consistently run programmes and work with recycling companies for batteries, printer inks, lamps and computers, using the dedicated blue bins for paper, aluminium and glass recycling placed throughout our offices.',
  },
  {
    el: 'Υποστηρίζουμε τον αντικαπνιστικό αγώνα «smoke free environment».',
    en: 'We support the anti-smoking "smoke free environment" effort.',
  },
];

const SocialResponsibility = () => {
  const { t, language } = useLanguage();
  const [communityOpen, setCommunityOpen] = useState(false);
  const [environmentOpen, setEnvironmentOpen] = useState(false);
  const [animalsOpen, setAnimalsOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={t('Κοινωνική Ευθύνη', 'Social Responsibility')}
        description={t(
          'Η δέσμευση της TFT στην κοινωνία, στο περιβάλλον και στους ανθρώπους μας.',
          "TFT's commitment to society, the environment and our people."
        )}
        canonical="https://www.tft.gr/company/certifications"
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-primary to-primary-light text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-6">
              {t('Εταιρική Κοινωνική Ευθύνη', 'Corporate Social Responsibility')}
            </h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              {t(
                'Η δέσμευσή μας για έναν καλύτερο κόσμο',
                'Our commitment to a better world'
              )}
            </p>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <p className="text-lg text-muted-foreground mb-6">
                {t('Με βασικό γνώμονα το τρίπτυχο ', 'With the triptych ')}<strong className="font-bold text-foreground">{t('«Άνθρωπος, Κοινωνία, Περιβάλλον»', '"People, Society, Environment"')}</strong>{t(', αναπτύσσουμε συνεχώς νέες πρωτοβουλίες οι οποίες ως στόχο έχουν να προσφέρουν στο κοινωνικό σύνολο αλλά και στο περιβάλλον.', ', we continuously develop new initiatives aimed at benefiting society as a whole as well as the environment.')}
              </p>
              <p className="text-lg text-muted-foreground">
                {t(
                  'Η Εταιρική Κοινωνική Ευθύνη είναι για εμάς ο τρόπος να εκφράσουμε την ανησυχία μας για όλα όσα συμβαίνουν γύρω μας, στους συνανθρώπους μας και στο περιβάλλον, και να συμβάλουμε στο να κάνουμε τον κόσμο που ζούμε καλύτερο, λίγο πιο φωτεινό και όσο το δυνατόν περισσότερο δίκαιο.',
                  'Corporate Social Responsibility is our way of expressing concern for everything happening around us, for our fellow human beings and the environment, and of contributing to making the world we live in better, a little brighter and as just as possible.'
                )}
              </p>
            </div>

            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-2xl w-fit">
                    <Users className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{t('Άνθρωποι', 'People')}</h3>
                  <p className="text-muted-foreground text-sm">
                    {t('Φροντίδα και ανάπτυξη της ομάδας μας.', 'Caring for and developing our team.')}
                  </p>
                </CardContent>
              </Card>

              <Card
                onClick={() => setEnvironmentOpen((v) => !v)}
                className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
                aria-expanded={environmentOpen}
              >
                <CardContent className="p-8 text-center">
                  <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-2xl w-fit">
                    <Leaf className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{t('Περιβάλλον', 'Environment')}</h3>
                  <p className="text-muted-foreground text-sm">
                    {t('Βιώσιμες πρακτικές και μείωση περιβαλλοντικού αποτυπώματος.', 'Sustainable practices and reduced environmental footprint.')}
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-1 text-xs font-medium text-primary">
                    <span>{environmentOpen ? t('Λιγότερα', 'Less') : t('Δείτε περισσότερα', 'See more')}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${environmentOpen ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>

              <Card
                onClick={() => setCommunityOpen((v) => !v)}
                className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
                aria-expanded={communityOpen}
              >
                <CardContent className="p-8 text-center">
                  <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-2xl w-fit">
                    <HeartHandshake className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{t('Κοινωνία', 'Community')}</h3>
                  <p className="text-muted-foreground text-sm">
                    {t('Δράσεις & Προσφορά', 'Actions & Contribution')}
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-1 text-xs font-medium text-primary">
                    <span>{communityOpen ? t('Λιγότερα', 'Less') : t('Δείτε περισσότερα', 'See more')}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${communityOpen ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>

              <Card
                onClick={() => setAnimalsOpen((v) => !v)}
                className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
                aria-expanded={animalsOpen}
              >
                <CardContent className="p-8 text-center">
                  <div className="mx-auto mb-4 p-4 bg-primary/10 rounded-2xl w-fit">
                    <PawPrint className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{t('Ζωοφιλική Φροντίδα', 'Animal Welfare')}</h3>
                  <p className="text-muted-foreground text-sm">
                    {t('Έμπρακτη στήριξη για τη φροντίδα και προστασία ζώων.', 'Active support for the care and protection of animals.')}
                  </p>
                  <div className="mt-3 flex items-center justify-center gap-1 text-xs font-medium text-primary">
                    <span>{animalsOpen ? t('Λιγότερα', 'Less') : t('Δείτε περισσότερα', 'See more')}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${animalsOpen ? 'rotate-180' : ''}`} />
                  </div>
                </CardContent>
              </Card>
            </div>

            <AnimatePresence initial={false}>
              {communityOpen && (
                <motion.div
                  key="community-panel"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="max-w-5xl mx-auto mt-8 overflow-hidden"
                >
                  <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <HeartHandshake className="h-6 w-6 text-primary" />
                      <h3 className="text-2xl font-bold text-foreground">
                        {t('Κοινωνία', 'Community')}
                      </h3>
                    </div>
                    <ul className="space-y-4">
                      {communityItems.map((item, i) => (
                        <li key={i} className="flex gap-3 text-base text-foreground/80 leading-relaxed">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                          <span>{language === 'el' ? item.el : item.en}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
              {environmentOpen && (
                <motion.div
                  key="environment-panel"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="max-w-5xl mx-auto mt-8 overflow-hidden"
                >
                  <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <Leaf className="h-6 w-6 text-primary" />
                      <h3 className="text-2xl font-bold text-foreground">
                        {t('Περιβάλλον', 'Environment')}
                      </h3>
                    </div>
                    <ul className="space-y-4">
                      {environmentItems.map((item, i) => (
                        <li key={i} className="flex gap-3 text-base text-foreground/80 leading-relaxed">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                          <span>{language === 'el' ? item.el : item.en}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
              {animalsOpen && (
                <motion.div
                  key="animals-panel"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="max-w-5xl mx-auto mt-8 overflow-hidden"
                >
                  <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <PawPrint className="h-6 w-6 text-primary" />
                      <h3 className="text-2xl font-bold text-foreground">
                        {t('Ζωοφιλική Φροντίδα', 'Animal Welfare')}
                      </h3>
                    </div>
                    <ul className="space-y-4">
                      <li className="flex gap-3 text-base text-foreground/80 leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <span>{t('Έχουμε ειδικά διαμορφωμένα σημεία στους εξωτερικούς χώρους του κτιρίου μας και στο εξωτερικό μας πάρκινγκ, για τη σίτιση των αδέσποτων της περιοχής.', 'We have specially designated spots in the outdoor areas of our building and our outdoor parking lot for feeding the local stray animals.')}</span>
                      </li>
                      <li className="flex gap-3 text-base text-foreground/80 leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <span>{t('Τα γραφεία μας είναι πάντα ανοιχτά για την παρουσία των τετράποδων φίλων μας, όποτε παραστεί ανάγκη.', 'Our offices are always open to our four-legged friends whenever the need arises.')}</span>
                      </li>
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SocialResponsibility;
